import React from 'react';
import {
  Box, Button, DialogActions, DialogContent, DialogTitle, Grid, IconButton,
  InputAdornment, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import TabletMacOutlinedIcon from '@mui/icons-material/TabletMacOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import firebase from '../../utilities/firebase';
import RequestManager from '../../utilities/requestManager';
import { StateManager } from '../../utilities/stateManager';
import { StackedDialog } from '../../utilities/ModalStackContext';
import InPersonSignDialog from './InPersonSignDialog';

const DOC_TYPE = 'consignor_bos';

/**
 * Consignor Bill of Sale.
 *
 * The consignor sells the car to Skyway for a stated amount, paid on a stated
 * date. Staff confirm both here, plus who signs for Skyway. Consignor and
 * vehicle details are read from the car on the backend.
 */
export default function ConsignorBosDialog({ open, onClose, car = {} }) {
  const stock = car.stock;
  const consignor = car.consignor || {};
  const email = consignor.email || '';
  const name = consignor.display_name
    || [consignor.first_name, consignor.last_name].filter(Boolean).join(' ').trim() || email;

  const [amount, setAmount] = React.useState('');
  const [paymentDate, setPaymentDate] = React.useState('');
  const [rep, setRep] = React.useState('');
  const [busy, setBusy] = React.useState('');
  const [inPersonOpen, setInPersonOpen] = React.useState(false);
  const [inPersonUrl, setInPersonUrl] = React.useState('');

  // Prefill the amount the same way the NTO check does.
  React.useEffect(() => {
    if (!open) return;
    setRep(car.consign_rep || '');
    setPaymentDate('');
    if (car.updated_nto) { setAmount(String(car.updated_nto)); return; }
    firebase.firestore().doc(`purchases/${stock}-NTO`).get().then((snap) => {
      setAmount(String((snap.exists && snap.data().amount) || car.nto || ''));
    });
  }, [open, stock, car.updated_nto, car.nto, car.consign_rep]);

  const formData = () => ({ amount: parseFloat(amount) || 0, paymentDate, rep: rep.trim() });

  const vehicle = `${car.year || ''} ${car.make || ''} ${car.model || ''}`.trim();

  const sessionVariables = (inPerson) => ({
    docType: DOC_TYPE,
    refId: stock,
    signers: [{ role: 'consignor', email, name }],
    inPerson,
    formData: formData(),
    name: `${vehicle} Bill of Sale`,
    subject: `${vehicle} Bill of Sale`,
    message: 'Please review and sign the bill of sale for your vehicle. It confirms the amount Skyway Classics will pay you and the expected payment date.\n\nIf you have any questions, please call your consignment representative.',
    sentById: StateManager.userID,
  });

  const validate = ({ requireEmail }) => {
    if (!(parseFloat(amount) > 0)) {
      StateManager.setAlertAndOpen('Enter the amount to be paid to the consignor.', 'error');
      return false;
    }
    if (!paymentDate) {
      StateManager.setAlertAndOpen('Enter the expected payment date.', 'error');
      return false;
    }
    if (requireEmail && !email) {
      StateManager.setAlertAndOpen('This consignor has no email address. Add one, or use Sign in Person.', 'error');
      return false;
    }
    return true;
  };

  const preview = async () => {
    if (!validate({ requireEmail: false })) return;
    setBusy('preview');
    const res = await RequestManager.post({
      function: 'previewFieldMap',
      variables: { docType: DOC_TYPE, refId: stock, formData: formData(), markers: false },
    });
    setBusy('');
    if (res && res.url) window.open(res.url, '_blank');
    else StateManager.setAlertAndOpen(res?.message || 'Could not build the preview.', 'error');
  };

  const send = async () => {
    if (!validate({ requireEmail: true })) return;
    setBusy('send');
    const res = await RequestManager.post({ function: 'createSignatureSession', variables: sessionVariables(false) });
    setBusy('');
    if (res && res.status === 'complete') {
      StateManager.setAlertAndOpen('Bill of sale sent for signature.', 'success');
      onClose();
    } else StateManager.setAlertAndOpen(res?.message || 'Could not send the bill of sale.', 'error');
  };

  const signHere = async () => {
    if (!validate({ requireEmail: false })) return;
    setBusy('inperson');
    const res = await RequestManager.post({ function: 'createSignatureSession', variables: sessionVariables(true) });
    setBusy('');
    if (res && res.status === 'complete') {
      setInPersonUrl(res.signUrl);
      setInPersonOpen(true);
    } else StateManager.setAlertAndOpen(res?.message || 'Could not start in-person signing.', 'error');
  };

  return (
    <>
      <StackedDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          Consignor Bill of Sale
          <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {name || 'The consignor'} signs a bill of sale transferring the car to Skyway Classics.
            Consignor and vehicle details are read from the car.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Amount to be paid to consignor" type="number" required
                value={amount} onChange={e => setAmount(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Expected payment date" type="date" required
                value={paymentDate} onChange={e => setPaymentDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Skyway representative (name, title)"
                value={rep} onChange={e => setRep(e.target.value)}
                helperText="Printed under the Skyway Classics signature line"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Signer" value={email ? `${name} <${email}>` : name}
                InputProps={{ readOnly: true }}
                helperText={email ? 'Emailed a signing link.' : 'No email on file — use Sign in Person.'}
                error={!email}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button startIcon={<PictureAsPdfOutlinedIcon />} onClick={preview} disabled={!!busy}>
            {busy === 'preview' ? 'Building…' : 'Preview'}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="outlined" startIcon={<TabletMacOutlinedIcon />} onClick={signHere} disabled={!!busy}>
            Sign in Person
          </Button>
          <Button variant="contained" startIcon={<DrawOutlinedIcon />} onClick={send} disabled={!!busy || !email}>
            {busy === 'send' ? 'Sending…' : 'Send for Signature'}
          </Button>
        </DialogActions>
      </StackedDialog>

      <InPersonSignDialog
        open={inPersonOpen}
        onClose={() => { setInPersonOpen(false); onClose(); }}
        signUrl={inPersonUrl}
        signerName={name}
      />
    </>
  );
}
